<template>
  <div>
    <h5 style="text-align: center;">学科竞赛奖状模板列表</h5>
    <hr>
    <el-button
      type="primary"
      style="display: block; margin: 10px auto;"
      @click="showAddDialog"
    >
      新建模板
    </el-button>

    <el-table :data="templateList" border style="width: 100%">
      <el-table-column prop="uuid" label="UUID" width="400"></el-table-column>
      <el-table-column prop="name" label="模板名称"></el-table-column>
      <el-table-column label="操作" width="400" align="center">
        <template #default="scope">
          <el-button type="primary" @click="openEditDialog(scope.row)">修改</el-button>
          <el-button type="default" @click="downloadTemplate(scope.row.uuid)">下载</el-button>
          <el-button type="danger" @click="deleteTemplate(scope.row.uuid)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 新建模板弹窗 -->
    <el-dialog title="新建模板" v-model="addDialogVisible" width="400px">
      <el-form label-width="80px">
        <el-form-item label="模板名称">
          <el-input v-model="newTemplate.name" placeholder="请输入模板名称"></el-input>
        </el-form-item>
        <el-form-item label="上传文件">
          <el-upload
            drag
            :auto-upload="false"
            :limit="1"
            :multiple="false"
            accept=".docx"
            :on-change="handleAddFileChange"
            :on-exceed="handleExceed"
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">
              拖拽文件到此处或 <em>点击上传</em>
            </div>
            <template #tip>
              <div class="el-upload__tip">仅支持上传一个 .docx 文件</div>
            </template>
          </el-upload>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="addDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="addTemplate">确定</el-button>
      </template>
    </el-dialog>

    <!-- 修改模板弹窗 -->
    <el-dialog title="修改模板" v-model="editDialogVisible" width="400px">
      <el-form label-width="80px">
        <el-form-item label="模板名称">
          <el-input v-model="editTemplate.name" placeholder="请输入新的模板名称"></el-input>
        </el-form-item>
        <el-form-item label="重新上传">
          <el-upload
            drag
            :auto-upload="false"
            :limit="1"
            :multiple="false"
            accept=".docx"
            :on-change="handleEditFileChange"
            :on-exceed="handleExceed"
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">
              拖拽文件到此处或 <em>点击上传</em>
            </div>
            <template #tip>
              <div class="el-upload__tip">可不上传文件，仅修改名称</div>
            </template>
          </el-upload>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="updateTemplate">保存修改</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import axios from 'axios'
import { ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'

const templateList = ref([])
const addDialogVisible = ref(false)
const editDialogVisible = ref(false)

const newTemplate = ref({ name: '', file: null })
const editTemplate = ref({ uuid: '', name: '', file: null })

// 获取模板列表
const fetchTemplates = async () => {
  try {
    const res = await axios.get('/api/Competition/templates')
    templateList.value = res.data.templates
  } catch {
    ElMessage.error('获取模板列表失败')
  }
}

// 打开新建对话框
const showAddDialog = () => {
  newTemplate.value = { name: '', file: null }
  addDialogVisible.value = true
}

// 打开修改对话框
const openEditDialog = (row) => {
  editTemplate.value = { uuid: row.uuid, name: row.name, file: null }
  editDialogVisible.value = true
}

// 处理上传文件
const handleAddFileChange = (file) => {
  newTemplate.value.file = file.raw
}
const handleEditFileChange = (file) => {
  editTemplate.value.file = file.raw
}
const handleExceed = () => {
  ElMessage.warning('只能上传一个文件')
}

// 新建模板
const addTemplate = async () => {
  if (!newTemplate.value.name || !newTemplate.value.file) {
    ElMessage.warning('请填写名称并上传文件')
    return
  }

  const formData = new FormData()
  formData.append('name', newTemplate.value.name)
  formData.append('file', newTemplate.value.file)

  try {
    await axios.post('/api/Competition/templates', formData)
    ElMessage.success('模板添加成功')
    addDialogVisible.value = false
    fetchTemplates()
  } catch {
    ElMessage.error('模板添加失败')
  }
}

// 修改模板
const updateTemplate = async () => {
  if (!editTemplate.value.name) {
    ElMessage.warning('模板名称不能为空')
    return
  }

  const formData = new FormData()
  formData.append('uuid', editTemplate.value.uuid)
  formData.append('name', editTemplate.value.name)
  if (editTemplate.value.file) formData.append('file', editTemplate.value.file)

  try {
    await axios.put('/api/Competition/templates', formData)
    ElMessage.success('模板修改成功')
    editDialogVisible.value = false
    fetchTemplates()
  } catch {
    ElMessage.error('模板修改失败')
  }
}

// 删除模板
const deleteTemplate = (uuid) => {
  ElMessageBox.confirm('确定要删除此模板吗？', '提示', {
    confirmButtonText: '删除',
    cancelButtonText: '取消',
    type: 'warning'
  })
    .then(async () => {
      try {
        await axios.delete(`/api/Competition/templates/${uuid}`)
        ElMessage.success('模板删除成功')
        fetchTemplates()
      } catch {
        ElMessage.error('删除模板失败，请检查模板是否有被引用')
      }
    })
    .catch(() => {
      ElMessage.info('删除操作已取消')
    })
}

// 下载模板
const downloadTemplate = async (uuid) => {
  try {
    const res = await axios.get(`/api/Competition/templates/download/${uuid}`, {
      responseType: 'arraybuffer'
    })
    const blob = new Blob([res.data], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = 'template.docx'
    link.click()
    URL.revokeObjectURL(link.href)
  } catch {
    ElMessage.error('下载失败')
  }
}

fetchTemplates()
</script>

<style scoped>
.el-button {
  margin-right: 5px;
}
</style>
